import { useAppTheme } from "../../../hooks/useAppTheme";
import React, { useEffect } from "react";
import {
  Dimensions,
  Modal,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  View,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../../components/shared/Spinner";
import { getAllChinaTrackerDetailsWithShipments } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useToast } from "react-native-toast-notifications";
import { formatLocalDate } from "../../../utils/common";
import type { ThemeColors } from "../../../utils/constants/colors";

interface ChinaShipmentsListModalProps {
  visible: boolean;
  onClose: () => void;
  order: any;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const { width, height } = Dimensions.get("window");
const isWeb = Platform.OS === "web";

const COL_WIDTH = 140;
const FIRST_COL_WIDTH = 160;

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: "rgba(15, 23, 42, 0.55)",
      justifyContent: "center",
      alignItems: "center",
      padding: isWeb ? 24 : 12,
    },
    modalContainer: {
      backgroundColor: colors.surface,
      width: isWeb ? Math.min(width * 0.82, 960) : "96%",
      maxHeight: height * 0.9,
      borderRadius: 16,
      overflow: "hidden",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 12 },
      shadowOpacity: 0.22,
      shadowRadius: 24,
      elevation: 12,
      display: "flex",
      flexDirection: "column",
    },

    // ── Header
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 20,
      paddingVertical: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.surfaceSubtle,
    },
    headerLeft: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
    },
    backButton: {
      width: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: colors.primaryBg,
      justifyContent: "center",
      alignItems: "center",
      marginRight: 4,
    },
    backArrow: {
      fontSize: 22,
      color: colors.primary,
      lineHeight: 26,
      fontWeight: "600",
    },
    title: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
    },
    titleAccent: {
      color: colors.primary,
    },
    subtitle: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 1,
    },
    closeBtn: {
      width: 30,
      height: 30,
      borderRadius: 6,
      backgroundColor: colors.borderSubtle,
      justifyContent: "center",
      alignItems: "center",
    },
    closeBtnText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontWeight: "600",
    },

    // ── Scroll
    scrollView: {
      flex: 1,
    },
    scrollContent: {
      padding: 16,
      paddingBottom: 8,
      gap: 0,
    },
    spinnerWrap: {
      justifyContent: "center",
      alignItems: "center",
      padding: 40,
    },

    // ── Section Card
    sectionCard: {
      backgroundColor: colors.surface,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.border,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
      marginBottom: 12,
      overflow: "hidden",
    },
    sectionCardHeader: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSubtle,
      backgroundColor: colors.surfaceSubtle,
      gap: 6,
    },
    sectionCardIcon: {
      fontSize: 14,
    },
    sectionCardTitle: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.text,
      letterSpacing: 0.2,
    },

    // ── Table
    tableWrapper: {
      minWidth: "100%",
    },
    tableHeaderRow: {
      flexDirection: "row",
      backgroundColor: colors.surfaceSubtle,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    tableHeaderCell: {
      width: COL_WIDTH,
      paddingVertical: 8,
      paddingHorizontal: 12,
      fontSize: 11,
      fontWeight: "700",
      color: colors.textSecondary,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    tableRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSubtle,
      backgroundColor: colors.surface,
    },
    tableRowZebra: {
      backgroundColor: colors.background,
    },
    cellText: {
      width: COL_WIDTH,
      paddingVertical: 9,
      paddingHorizontal: 12,
      fontSize: 12,
      color: colors.text,
    },
    firstCell: {
      width: FIRST_COL_WIDTH,
    },
    cellBold: {
      fontWeight: "600",
      color: colors.text,
    },

    // ── Badge
    badge: {
      alignSelf: "flex-start",
      backgroundColor: colors.primaryBg,
      borderRadius: 4,
      paddingHorizontal: 8,
      paddingVertical: 2,
    },
    badgeText: {
      fontSize: 11,
      color: colors.primary,
      fontWeight: "600",
    },

    // ── Run block
    runBlock: {
      marginBottom: 16,
    },
    runPill: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.primaryBg,
      borderRadius: 8,
      paddingVertical: 9,
      paddingHorizontal: 14,
      marginBottom: 10,
      gap: 8,
    },
    runPillIcon: {
      fontSize: 14,
      color: colors.primary,
    },
    runPillText: {
      fontSize: 13,
      fontWeight: "700",
      color: colors.primary,
    },
    runPillDivider: {
      width: 1,
      height: 14,
      backgroundColor: colors.border,
      marginHorizontal: 4,
    },
    runPillSub: {
      fontSize: 12,
      color: colors.textSecondary,
    },

    // ── Empty states
    emptyShipments: {
      paddingHorizontal: 14,
      paddingVertical: 10,
      backgroundColor: colors.surfaceSubtle,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.warning,
      marginBottom: 10,
    },
    emptyShipmentsText: {
      fontSize: 12,
      color: colors.warning,
      fontStyle: "italic",
    },
    emptyState: {
      alignItems: "center",
      paddingVertical: 48,
    },
    emptyStateIcon: {
      fontSize: 36,
      marginBottom: 10,
    },
    emptyStateText: {
      fontSize: 14,
      color: colors.textMuted,
      fontStyle: "italic",
    },

    // ── Footer
    footer: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingHorizontal: 20,
      paddingVertical: 12,
      flexDirection: "row",
      justifyContent: "flex-end",
      backgroundColor: colors.surfaceSubtle,
    },
    footerCloseBtn: {
      borderWidth: 1,
      borderColor: colors.textMuted,
      borderRadius: 8,
      paddingHorizontal: 20,
      paddingVertical: 8,
      backgroundColor: colors.surface,
    },
    footerCloseBtnText: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.text,
    },
  });
}

type S = ReturnType<typeof createStyles>;

// ─── Reusable Badge ──────────────────────────────────────────────────────────
const StatusBadge = ({ value, s }: { value?: any; s: S }) => {
  if (!value) return <Text style={s.cellText}>—</Text>;
  return (
    <View style={s.badge}>
      <Text style={s.badgeText}>{value}</Text>
    </View>
  );
};

// ─── Table primitives ────────────────────────────────────────────────────────
const TableHeader = ({ columns, s }: { columns: string[]; s: S }) => (
  <View style={s.tableHeaderRow}>
    {columns.map((col, i) => (
      <Text
        key={i}
        style={[s.tableHeaderCell, i === 0 && s.firstCell]}
      >
        {col}
      </Text>
    ))}
  </View>
);

const TableRow = ({
  cells,
  zebra,
  s,
}: {
  cells: (string | undefined | null | number)[];
  zebra: boolean;
  s: S;
}) => (
  <View style={[s.tableRow, zebra && s.tableRowZebra]}>
    {cells.map((cell, i) => (
      <Text
        key={i}
        style={[
          s.cellText,
          i === 0 && s.firstCell,
          i === 0 && s.cellBold,
        ]}
      >
        {cell ?? "—"}
      </Text>
    ))}
  </View>
);

// ─── Section card wrapper ─────────────────────────────────────────────────────
const SectionCard = ({
  icon,
  title,
  accent,
  children,
  s,
}: {
  icon: string;
  title: string;
  accent?: string;
  children: React.ReactNode;
  s: S;
}) => (
  <View style={[s.sectionCard, accent ? { borderLeftColor: accent } : {}]}>
    <View style={s.sectionCardHeader}>
      <Text style={s.sectionCardIcon}>{icon}</Text>
      <Text style={[s.sectionCardTitle, accent ? { color: accent } : {}]}>
        {title}
      </Text>
    </View>
    {children}
  </View>
);

// ─── Main Modal ───────────────────────────────────────────────────────────────
const ChinaShipmentsListModal: React.FC<ChinaShipmentsListModalProps> = ({
  visible,
  onClose,
  order,
}) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const dispatch = useDispatch<any>();
  const { orderDetailShipments, loadingShipmentsAction } = useSelector(
    (state: any) => state.chinaTracker,
  );
  const toast = useToast();

  useEffect(() => {
    if (order) {
      dispatch(
        getAllChinaTrackerDetailsWithShipments({
          orderNumber: order.OrderNumber1,
          key: order.Key,
        }),
      )
        .unwrap()
        .catch((err: any) => {
          toast.show(err, { type: "danger" });
        });
    }
  }, [order]);

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* ── Header ─────────────────────────────────────── */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <TouchableOpacity onPress={onClose} style={styles.backButton}>
                <Text style={styles.backArrow}>‹</Text>
              </TouchableOpacity>
              <View>
                <Text style={styles.title}>
                  Order{" "}
                  <Text style={styles.titleAccent}>#{order?.OrderNumber1}</Text>
                </Text>
                {order?.Key && (
                  <Text style={styles.subtitle}>Key: {order.Key}</Text>
                )}
              </View>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
              <Text style={styles.closeBtnText}>✕</Text>
            </TouchableOpacity>
          </View>

          {/* ── Body ───────────────────────────────────────── */}
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {loadingShipmentsAction ? (
              <View style={styles.spinnerWrap}>
                <Spinner
                  size="small"
                  color="#2563EB"
                  styles={{ marginTop: 50, marginBottom: 50 }}
                />
              </View>
            ) : (
              <>
                {/* ── Order Summary Table ─────────────────── */}
                <SectionCard icon="📋" title="Order Summary" accent="#2563EB" s={styles}>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <View style={styles.tableWrapper}>
                      <TableHeader columns={["Field", "Value"]} s={styles} />
                      <TableRow
                        zebra={false}
                        cells={["Order Number", order?.OrderNumber1]}
                        s={styles}
                      />
                      <TableRow zebra={true} cells={["Key", order?.Key]} s={styles} />
                      <TableRow
                        zebra={false}
                        cells={["Priority", order?.PriorityOrder || "—"]}
                        s={styles}
                      />
                    </View>
                  </ScrollView>
                </SectionCard>

                {/* ── Run Orders ─────────────────────────── */}
                {orderDetailShipments && orderDetailShipments.length > 0 ? (
                  orderDetailShipments.map((detail: any, idx: number) => (
                    <View key={detail._id} style={styles.runBlock}>
                      {/* Run order header pill */}
                      <View style={styles.runPill}>
                        <Text style={styles.runPillIcon}>🏭</Text>
                        <Text style={styles.runPillText}>
                          Run Order #{detail.runOrder}
                        </Text>
                        <View style={styles.runPillDivider} />
                        <Text style={styles.runPillSub}>
                          {detail.allocateProductionLine || "No Line"}
                        </Text>
                      </View>

                      {/* Run details table */}
                      <SectionCard
                        icon=""
                        title="Production Details"
                        accent="#0F9D58"
                        s={styles}
                      >
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={true}
                        >
                          <View style={styles.tableWrapper}>
                            <TableHeader
                              s={styles}
                              columns={[
                                "Field",
                                "Planned Qty",
                                "Production Line",
                                "Plan Start",
                                "Est. Completion",
                                "ETD",
                                "ETA",
                                "Expected Arrival",
                              ]}
                            />
                            <TableRow
                              s={styles}
                              zebra={false}
                              cells={[
                                "Value",
                                detail.plannedQty,
                                detail.allocateProductionLine,
                                formatLocalDate(detail.planStartDay),
                                formatLocalDate(detail.ESTCompletionDate),
                                formatLocalDate(detail.ETD),
                                formatLocalDate(detail.ETA),
                                formatLocalDate(detail.ExpectedArrival),
                              ]}
                            />
                          </View>
                        </ScrollView>
                      </SectionCard>

                      {/* Shipments table */}
                      {detail.shipments && detail.shipments.length > 0 ? (
                        <SectionCard
                          icon="🚢"
                          title={`Shipments (${detail.shipments.length})`}
                          accent="#F59E0B"
                          s={styles}
                        >
                          <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                          >
                            <View style={styles.tableWrapper}>
                              <TableHeader
                                s={styles}
                                columns={[
                                  "Shipment No",
                                  "Qty",
                                  "Container",
                                  "ETD",
                                  "ETA",
                                ]}
                              />
                              {detail.shipments.map(
                                (item: any, sIdx: number) => (
                                  <TableRow
                                    key={item._id}
                                    s={styles}
                                    zebra={sIdx % 2 === 1}
                                    cells={[
                                      item.shipmentNo,
                                      item.shipmentQty,
                                      item.containerNo,
                                      formatLocalDate(item.ETD),
                                      formatLocalDate(item.ETA),
                                    ]}
                                  />
                                ),
                              )}
                            </View>
                          </ScrollView>
                        </SectionCard>
                      ) : (
                        <View style={styles.emptyShipments}>
                          <Text style={styles.emptyShipmentsText}>
                            No shipments recorded for this run order.
                          </Text>
                        </View>
                      )}
                    </View>
                  ))
                ) : (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyStateIcon}>📦</Text>
                    <Text style={styles.emptyStateText}>
                      No factory order details found.
                    </Text>
                  </View>
                )}
              </>
            )}
          </ScrollView>


        </View>
      </View>
    </Modal>
  );
};

export default ChinaShipmentsListModal;
